import { useState, useRef, useEffect } from 'react';
import type { Language } from '@vikings/types';
import { t } from '@vikings/game-data';
import './PixelModal.css';

interface PasswordModalProps {
  onSubmit: (password: string) => Promise<void> | void;
  onClose: () => void;
  isLoading?: boolean;
  errorMessage?: string;
  language?: Language;
}

/**
 * Pixel-art password modal for dragon authentication.
 */
export function PasswordModal({
  onSubmit,
  onClose,
  isLoading = false,
  errorMessage = '',
  language = 'uk',
}: PasswordModalProps) {
  const [password, setPassword] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!password.trim() || isLoading) return;
    onSubmit(password.trim());
  };

  return (
    <div className="pixel-overlay" onClick={onClose}>
      <div className="pixel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="pixel-modal-title" style={{ textAlign: 'center' }}>
          🐉 {t(language, 'enterPassword')}
        </div>
        <div className="pixel-modal-divider" />

        <div className="pixel-modal-text" style={{ textAlign: 'center', marginBottom: '14px' }}>
          {t(language, 'passwordPrompt')}
        </div>

        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            className="pixel-input"
            placeholder={t(language, 'passwordPlaceholder')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck="false"
          />

          {errorMessage && (
            <div className="pixel-warning-box">
              ⚠️ {errorMessage}
            </div>
          )}

          <div className="pixel-modal-spacer" />

          <div className="pixel-button-group">
            <button
              type="submit"
              className="pixel-button pixel-button-gold"
              disabled={isLoading || !password.trim()}
            >
              {isLoading ? t(language, 'loading') : t(language, 'loginBtn')}
            </button>
            <button
              type="button"
              className="pixel-button pixel-button-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              {t(language, 'cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
